import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  createDarContemplated,
  getDarContemplated,
  updateDarContemplated,
  getSapEmployeeFetch,
  bypassDarContemplated
} from "../../../core/data/redux/darSlice";
import { uploadFile, getPresignedUrl } from "../../../core/data/redux/awsBucketSlice";
import { AppDispatch } from "../../../core/data/redux/store";
import CharacterCounter from "../../../core/common/CharacterCounter";

const DarContemplated: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const casenoParam = searchParams.get("caseno");

  const [loading, setLoading] = useState(false);
  const [initialFetchLoading, setInitialFetchLoading] = useState(false);
  const [fetchingEmployee, setFetchingEmployee] = useState(false);
  const [searchCaseNo, setSearchCaseNo] = useState("");

  const [formData, setFormData] = useState({
    caseno: "",
    emp_no: "",
    daname: "",
    dadesignation: "",
    daempno: "",
    dadepartment: "",
    casetype: "Administrative",
    casedescription: "",
    approval_file: "",
    da_source: "Internal"
  });
  const [fetchingDa, setFetchingDa] = useState(false);

  const fetchDaDetails = async (daNumber?: string) => {
    const empNo = daNumber || formData.daempno;
    if (!empNo || empNo.trim().length < 3) {
      toast.warning("Please enter a valid DA Employee No.");
      return;
    }
    setFetchingDa(true);
    try {
      const res = await dispatch(getSapEmployeeFetch({ emp_no: empNo.trim(), role: "DA" })).unwrap();
      if (res?.status === 200 && res?.data) {
        setFormData(prev => ({
          ...prev,
          daname: res.data.empname || "",
          dadesignation: res.data.designation || "",
          dadepartment: res.data.deptname || ""
        }));
      } else {
        const errorMsg = res?.message || res?.data?.message || res?.error || "DA Employee not found.";
        toast.error(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = typeof err === 'string' ? err : (err?.message || err?.error || err?.data?.message || err?.response?.data?.message || "Error fetching DA data.");
      toast.error(errorMsg);
    } finally {
      setFetchingDa(false);
    }
  };

  const [employeeDetails, setEmployeeDetails] = useState<any>({
    empname: "",
    designation: "",
    doj: "",
    basicpay: "",
    annualincdate: "",
    lastpromodate: "",
    payscl: "",
    deptcode: ""
  });

  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docName, setDocName] = useState("");

  const handleViewFile = async (fileKey: string) => {
    if (!fileKey) return;
    const toastId = toast.loading("Fetching document...");
    try {
      const url = await dispatch(getPresignedUrl(fileKey)).unwrap();
      toast.dismiss(toastId);
      window.open(url, "_blank");
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Could not fetch document URL.");
    }
  };

  const [showBypassModal, setShowBypassModal] = useState(false);
  const [bypassReason, setBypassReason] = useState("");
  const [bypassing, setBypassing] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB.");
      if (e.target) e.target.value = "";
      return;
    }

    const validTypes = ["application/pdf"];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file format. Only PDF files are allowed.");
      if (e.target) e.target.value = "";
      return;
    }

    setUploadingDoc(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(",")[1];
      try {
        const result = await dispatch(
          uploadFile({ filestring: base64, mime_type: file.type, module: "dar" })
        );
        if (uploadFile.fulfilled.match(result)) {
          const key: string =
            result.payload?.key ||
            result.payload?.data?.key ||
            result.payload?.url ||
            result.payload?.data?.url ||
            "";
          if (key) {
            setFormData((prev) => ({ ...prev, approval_file: key }));
            setDocName(file.name);
            toast.success("Document uploaded successfully.");
          } else {
            toast.error("Failed to retrieve document path from server.");
          }
        } else {
          toast.error("Failed to upload document.");
        }
      } catch (error) {
        toast.error("Error uploading document.");
      } finally {
        setUploadingDoc(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    if (type === "checkbox") {
      setFormData({ ...formData, [name]: (e.target as HTMLInputElement).checked });
    } else if (name === "da_source") {
      setFormData(prev => ({
        ...prev,
        da_source: value,
        daempno: "",
        daname: "",
        dadesignation: ""
      }));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const fetchEmployeeDetails = async () => {
    if (!formData.emp_no || formData.emp_no.length < 3) return;
    setFetchingEmployee(true);
    try {
      const res = await dispatch(getSapEmployeeFetch({ emp_no: formData.emp_no })).unwrap();
      if (res?.status === 200 && res?.data) {
        setEmployeeDetails({
          empname: res.data.empname || "",
          designation: res.data.designation || "",
          doj: res.data.doj || "",
          basicpay: res.data.basicpay || "",
          annualincdate: res.data.annualincdate || "",
          lastpromodate: res.data.lastpromodate || "",
          payscl: res.data.payscl || "",
          deptcode: res.data.deptname || ""
        });
      } else {
        const errorMsg = res?.message || res?.data?.message || res?.error || "Employee not found.";
        toast.error(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = typeof err === 'string' ? err : (err?.message || err?.error || err?.data?.message || err?.response?.data?.message || "Error fetching employee data.");
      toast.error(errorMsg);
    } finally {
      setFetchingEmployee(false);
    }
  };

  const handleFetchCase = async () => {
    if (!searchCaseNo.trim()) {
      toast.warning("Please enter a Case No.");
      return;
    }
    setInitialFetchLoading(true);
    try {
      const res = await dispatch(getDarContemplated({ caseno: searchCaseNo.trim() })).unwrap();
      if (res?.status === 200 && res?.data) {
        const data = res.data;
        setFormData({
          caseno: data.caseno || "",
          emp_no: data.empno || "",
          daname: data.daname || "",
          dadesignation: data.dadesignation || "",
          daempno: data.daempno || "",
          dadepartment: data.dadepartment || data.da_department || "",
          casetype: data.casetype || "Administrative",
          casedescription: data.casedescription || "",
          approval_file: data.approvalfile || "",
          da_source: data.da_source || (data.daempno ? "Internal" : "External")
        });
        setEmployeeDetails({
          empname: data.empname || "",
          designation: data.designation || "",
          doj: data.doj || "",
          basicpay: data.basicpay || "",
          annualincdate: data.annualincdate || "",
          lastpromodate: data.lastpromodate || "",
          payscl: data.payscl || "",
          deptcode: data.deptcode || ""
        });
        if (data.approvalfile) {
          setDocName(data.approvalfile.split('/').pop() || "Approval_Document.pdf");
        }
        navigate(`/dar-cases/new?caseno=${searchCaseNo.trim()}`);
      } else {
        toast.error(res?.message || "Could not find contemplated case.");
      }
    } catch (err: any) {
      toast.error(err?.message || "Error fetching case.");
    } finally {
      setInitialFetchLoading(false);
    }
  };

  useEffect(() => {
    toast.dismiss();
    if (casenoParam) {
      setInitialFetchLoading(true);
      dispatch(getDarContemplated({ caseno: casenoParam }))
        .unwrap()
        .then((res: any) => {
          if (res?.status === 200 && res?.data) {
            const data = res.data;
            setFormData({
              caseno: data.caseno || "",
              emp_no: data.empno || "",
              daname: data.daname || "",
              dadesignation: data.dadesignation || "",
              daempno: data.daempno || "",
              dadepartment: data.dadepartment || data.da_department || "",
              casetype: data.casetype || "Administrative",
              casedescription: data.casedescription || "",
              approval_file: data.approvalfile || "",
              da_source: data.da_source || (data.daempno ? "Internal" : "External")
            });
            if (data.approvalfile) {
              setDocName(data.approvalfile.split('/').pop() || "Approval_Document.pdf");
            }

            // Fetch subject employee details from SAP to display current details
            if (data.empno) {
              dispatch(getSapEmployeeFetch({ emp_no: data.empno }))
                .unwrap()
                .then((empRes: any) => {
                  if (empRes?.status === 200 && empRes?.data) {
                    setEmployeeDetails({
                      empname: empRes.data.empname || "",
                      designation: empRes.data.designation || "",
                      doj: empRes.data.doj || "",
                      basicpay: empRes.data.basicpay || "",
                      annualincdate: empRes.data.annualincdate || "",
                      lastpromodate: empRes.data.lastpromodate || "",
                      payscl: empRes.data.payscl || "",
                      deptcode: empRes.data.deptname || ""
                    });
                  }
                })
                .catch(() => {});
            }

            // Fetch DA details from SAP to display current DA department
            if (data.daempno) {
              dispatch(getSapEmployeeFetch({ emp_no: data.daempno, role: "DA" }))
                .unwrap()
                .then((daRes: any) => {
                  if (daRes?.status === 200 && daRes?.data) {
                    setFormData(prev => ({
                      ...prev,
                      daname: daRes.data.empname || "",
                      dadesignation: daRes.data.designation || "",
                      dadepartment: daRes.data.deptname || ""
                    }));
                  }
                })
                .catch(() => {});
            }
          } else {
            toast.error(res?.message || "Failed to fetch case details");
          }
        })
        .catch((err: any) => {
          toast.error(err?.message || "Error fetching case details");
        })
        .finally(() => {
          setInitialFetchLoading(false);
        });
    }
  }, [casenoParam, dispatch]);

  const handleSubmit = async () => {
    if (!formData.emp_no || !formData.daname || !formData.dadesignation || !formData.dadepartment || !formData.casedescription) {
      toast.error("Please fill all mandatory fields.");
      return;
    }

    if (!formData.daempno) {
      toast.error("DA Employee No is required.");
      return;
    }

    if (!casenoParam && !formData.approval_file) {
      toast.error("Approval document is mandatory for new cases.");
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        emp_no: formData.emp_no,
        daname: formData.daname,
        dadesignation: formData.dadesignation,
        daempno: formData.daempno,
        casetype: formData.casetype,
        casedescription: formData.casedescription,
        approval_file: formData.approval_file,
        da_source: formData.da_source
      };

      let action;

      if (casenoParam) {
        action = updateDarContemplated;
        payload.caseno = casenoParam; // For update, caseno is required
        delete payload.emp_no; // Protected field on update
      } else {
        action = createDarContemplated;
      }

      const response = await dispatch(action(payload)).unwrap();

      if (response?.status === 200 || response?.status === 201) {
        toast.success(response?.message || `Contemplated Case ${casenoParam ? 'updated' : 'created'} successfully!`);
        navigate("/dar-cases/overview");
      } else {
        toast.error(response?.message || `Failed to ${casenoParam ? 'update' : 'create'} case.`);
      }
    } catch (error: any) {
      toast.error(error?.message || `Error ${casenoParam ? 'updating' : 'creating'} contemplated case.`);
    } finally {
      setLoading(false);
    }
  };

  const handleBypassSubmit = async () => {
    if (!bypassReason.trim()) {
      toast.error("Bypass reason is mandatory.");
      return;
    }

    if (!casenoParam && !formData.caseno) {
      toast.error("Please submit and create the case first before bypassing.");
      setShowBypassModal(false);
      return;
    }

    setBypassing(true);
    try {
      const payload = {
        case_no: casenoParam || formData.caseno,
        reason: bypassReason
      };

      const response = await dispatch(bypassDarContemplated(payload)).unwrap();

      if (response?.status === 200 || response?.status === 201) {
        toast.success(response?.message || "Case successfully bypassed to Pending stage.");
        setShowBypassModal(false);
        navigate("/dar-cases/pending");
      } else {
        toast.error(response?.message || "Failed to bypass case.");
      }
    } catch (error: any) {
      const errorMessage = typeof error === 'string' ? error : error?.message;
      toast.error(errorMessage || "Error bypassing case.");
    } finally {
      setBypassing(false);
    }
  };

  return (
    <div className="page-wrapper bg-light min-vh-100">
      <div className="content container-fluid pb-5">

        {/* HEADER */}
        <div className="page-header mb-4">
          <div className="d-flex align-items-center">
            <div>
              <h3 className="page-title fw-bold text-dark mb-1">
                {casenoParam ? "Edit D&AR Contemplated Case" : "Initiate D&AR Contemplated Case"}
              </h3>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item"><Link to="/dar-cases/overview" className="text-decoration-none">Dashboard</Link></li>
                  <li className="breadcrumb-item active" aria-current="page">Contemplated Stage</li>
                </ol>
              </nav>
            </div>
          </div>
        </div>

        {initialFetchLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="row justify-content-center">
            <div className="col-12">

              {/* FORM SECTION */}
              <div className="card">
                <div className="card-body">

                  {/* SECTION 1: EMPLOYEE DETAILS */}
                  <div className="mb-5">
                    <h5 className="border-bottom pb-2 mb-3 fw-bold text-dark">1. Subject Employee Details</h5>

                    <div className="mb-4">
                      <div className="row align-items-end">
                        <div className="col-md-5">
                          <label className="form-label">Employee No <span className="text-danger">*</span></label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              name="emp_no"
                              value={formData.emp_no}
                              onChange={handleInputChange}
                              placeholder="Enter Employee No"
                              disabled={!!casenoParam}
                            />
                            {!casenoParam && (
                              <button className="btn btn-primary" type="button" onClick={fetchEmployeeDetails} disabled={fetchingEmployee}>
                                {fetchingEmployee ? <i className="ti ti-loader fa-spin"></i> : "Fetch Data"}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {employeeDetails.empname && (
                      <div className="row g-3 mt-3 pt-3 border-top">
                        <div className="col-md-4">
                          <label className="form-label text-muted small mb-1">Employee Name</label>
                          <input type="text" className="form-control bg-light" value={employeeDetails.empname} placeholder="Pending SAP Fetch..." readOnly />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label text-muted small mb-1">Designation</label>
                          <input type="text" className="form-control bg-light" value={employeeDetails.designation} placeholder="Pending SAP Fetch..." readOnly />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label text-muted small mb-1">Department</label>
                          <input type="text" className="form-control bg-light" value={employeeDetails.deptcode} placeholder="Pending SAP Fetch..." readOnly />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label text-muted small mb-1">Date of Joining</label>
                          <input type="text" className="form-control bg-light" value={employeeDetails.doj} placeholder="Pending SAP Fetch..." readOnly />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label text-muted small mb-1">Basic Pay</label>
                          <input type="text" className="form-control bg-light" value={employeeDetails.basicpay} placeholder="Pending SAP Fetch..." readOnly />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label text-muted small mb-1">Pay Scale</label>
                          <input type="text" className="form-control bg-light" value={employeeDetails.payscl} placeholder="Pending SAP Fetch..." readOnly />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label text-muted small mb-1">Annual Increment</label>
                          <input type="text" className="form-control bg-light" value={employeeDetails.annualincdate} placeholder="Pending SAP Fetch..." readOnly />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* SECTION 2: DISCIPLINARY AUTHORITY */}
                  <div className="mb-5">
                    <h5 className="border-bottom pb-2 mb-3 fw-bold text-dark">2. Disciplinary Authority (DA)</h5>

                    <div className="row g-3">
                      <div className="col-md-3">
                        <label className="form-label">DA Employee No <span className="text-danger">*</span></label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="daempno"
                            value={formData.daempno}
                            onChange={handleInputChange}
                            onBlur={() => {
                              if (formData.daempno && formData.daempno.trim().length >= 3) {
                                fetchDaDetails();
                              }
                            }}
                            placeholder="e.g. 10234"
                          />
                          <button
                            className="btn btn-primary"
                            type="button"
                            onClick={() => fetchDaDetails()}
                            disabled={fetchingDa}
                          >
                            {fetchingDa ? <i className="ti ti-loader fa-spin"></i> : "Fetch"}
                          </button>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">DA Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          name="daname"
                          value={formData.daname}
                          onChange={handleInputChange}
                          placeholder="Fetched Name"
                          readOnly
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">DA Designation <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          name="dadesignation"
                          value={formData.dadesignation}
                          onChange={handleInputChange}
                          placeholder="Fetched Designation"
                          readOnly
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Department <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          name="dadepartment"
                          value={formData.dadepartment}
                          onChange={handleInputChange}
                          placeholder="Fetched Department"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  {/* SECTION 3: CASE DETAILS */}
                  <div className="mb-5">
                    <h5 className="border-bottom pb-2 mb-3 fw-bold text-dark">3. Case Description</h5>

                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">Type of Case <span className="text-danger">*</span></label>
                        <select
                          className="form-select"
                          name="casetype"
                          value={formData.casetype}
                          onChange={handleInputChange}
                        >
                          <option value="Administrative">Administrative</option>
                          <option value="Vigilance">Vigilance</option>
                        </select>
                      </div>
                      <div className="col-md-12">
                        <label className="form-label">Brief Description of Allegations <span className="text-danger">*</span></label>
                        <textarea
                          className="form-control"
                          rows={4}
                          name="casedescription"
                          value={formData.casedescription}
                          onChange={handleInputChange}
                          placeholder="Provide a brief, factual description of the incident or allegations..."
                          maxLength={1000}
                        />
                        <CharacterCounter value={formData.casedescription} maxLength={1000} />
                      </div>
                    </div>
                  </div>

                  {/* SECTION 4: DOCUMENTS & SUBMISSION */}
                  <div className="mb-4">
                    <h5 className="border-bottom pb-2 mb-3 fw-bold text-dark">4. Attachments & Workflow</h5>

                    <div className="row g-3 align-items-start">
                      <div className="col-md-6">
                        <label className="form-label">
                          Approval Sheet Document <span className="text-danger">*</span>
                        </label>

                        <div className="d-flex align-items-center gap-3">
                          <input
                            type="file"
                            id="approvalDoc"
                            className="d-none"
                            accept=".pdf"
                            onChange={handleFileUpload}
                            disabled={uploadingDoc}
                          />
                          {!docName && (
                            <button
                              type="button"
                              className="btn btn-outline-primary d-flex align-items-center gap-2 px-4 shadow-sm"
                              onClick={() => document.getElementById("approvalDoc")?.click()}
                              disabled={uploadingDoc}
                            >
                              {uploadingDoc ? (
                                <><i className="ti ti-loader fa-spin"></i> Uploading...</>
                              ) : (
                                <><i className="ti ti-upload"></i> Browse File</>
                              )}
                            </button>
                          )}

                          {docName && !uploadingDoc && (
                            <div className="bg-light border border-success-subtle rounded px-3 d-flex align-items-center justify-content-between shadow-sm" style={{ height: "38px", minWidth: "300px", maxWidth: "450px" }}>
                              <div
                                className="d-flex align-items-center gap-2 text-dark overflow-hidden me-2"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleViewFile(formData.approval_file)}
                                title="Click to view file"
                              >
                                <i className="ti ti-file-check text-success fs-5"></i>
                                <div className="text-truncate" style={{ maxWidth: '200px' }}>
                                  <span className="fw-medium small text-primary text-decoration-underline" title={docName}>{docName}</span>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
                                  style={{ width: "28px", height: "28px", padding: 0 }}
                                  title="View File"
                                  onClick={(e) => { e.preventDefault(); handleViewFile(formData.approval_file); }}
                                >
                                  <i className="ti ti-eye" style={{ fontSize: "1rem" }}></i>
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
                                  style={{ width: "28px", height: "28px", padding: 0 }}
                                  title="Remove File"
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDocName(""); setFormData(p => ({ ...p, approval_file: "" })); }}
                                >
                                  <i className="ti ti-x" style={{ fontSize: "1rem" }}></i>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        {!docName && (
                          <div className="text-muted small mt-2">
                            Only PDF files under 5MB are acceptable.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                      <button className="btn btn-light" disabled={loading} onClick={() => navigate(-1)}>
                        Cancel
                      </button>



                      <button className="btn btn-primary d-flex align-items-center gap-2" onClick={handleSubmit} disabled={loading}>
                        {loading ? (
                          <><i className="ti ti-loader fa-spin"></i> Processing...</>
                        ) : (
                          casenoParam ? "Update Case Details" : "Initiate Case"
                        )}
                      </button>
                    </div>

                  </div>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* BYPASS CONTEMPLATED STAGE OVERWRITE MODAL */}
        {showBypassModal && (
          <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1040, backgroundColor: 'rgba(0,0,0,0.6)' }}></div>
            <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1045 }}>
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                  <div className="modal-header bg-light border-bottom-0 p-3">
                    <h4 className="modal-title fw-bold text-dark d-flex align-items-center gap-2 m-0">
                      <i className="ti ti-alert-triangle text-warning" style={{ fontSize: '1.5rem' }}></i> Bypass Contemplated Stage
                    </h4>
                    <button type="button" className="btn-close shadow-none" onClick={() => setShowBypassModal(false)}></button>
                  </div>
                  <div className="modal-body p-3">
                    <div className="alert alert-warning border-0 rounded-3 shadow-sm mb-3 px-3 py-2 text-sm d-flex gap-2 align-items-center">
                      <i className="ti ti-info-circle text-warning" style={{ fontSize: '1.25rem' }}></i>
                      <div style={{ fontSize: '0.85rem' }}>
                        This permanently bypasses the review timeline and advances to <strong>Pending Stage</strong>.
                      </div>
                    </div>

                    <div className="row g-3 mb-2">
                      <div className="col-md-12">
                        <label className="form-label text-muted small fw-bold mb-1 text-uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>Case Number</label>
                        <div className="bg-light rounded px-3 py-2 fw-semibold text-dark font-monospace border small">
                          {casenoParam || formData.caseno || "Unassigned"}
                        </div>
                      </div>

                      <div className="col-md-12">
                        <label className="form-label text-muted small fw-bold mb-1 text-uppercase tracking-wider" style={{ fontSize: '0.7rem' }}>
                          Reason for Bypass <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className="form-control bg-white shadow-none border-secondary-subtle small"
                          rows={3}
                          value={bypassReason}
                          onChange={(e) => setBypassReason(e.target.value)}
                          placeholder="Provide a detailed justification..."
                          maxLength={500}
                          required
                        ></textarea>
                        <CharacterCounter value={bypassReason} maxLength={500} />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer border-top-0 p-3 pt-0">
                    <button type="button" className="btn btn-light rounded-pill px-4 fw-medium btn-sm" onClick={() => setShowBypassModal(false)} disabled={bypassing}>
                      Cancel
                    </button>
                    <button type="button" className="btn btn-warning rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center gap-2 btn-sm" onClick={handleBypassSubmit} disabled={bypassing}>
                      {bypassing ? <><i className="ti ti-loader fa-spin"></i> Processing...</> : <><i className="ti ti-check"></i> Confirm Bypass</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DarContemplated;

